import * as React from 'react';
import DeclineMessage from './DeclineMessage';
import DefaultMessage from './DefaultMessage';
import { faEye } from '@fortawesome/pro-solid-svg-icons/faEye';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage } from 'react-intl';
import { getBiddingInfo } from '@/redux/modules/biddingInfo';
import { getCatalog } from '@/redux/modules/catalog';
import { getCatalogRegistration } from '@/redux/modules/catalogRegistration';
import { getItem } from '@/redux/modules/item';
import { getLiveItemStatus } from '@/redux/modules/liveItemStatus';
import { getSeller } from '@/redux/modules/seller';
import isEqual from 'react-fast-compare';
import { isToday } from 'date-fns';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import styles from './BidMessage.module.scss';
import { useSelector } from 'react-redux';

type Props = {
    itemId: number;
    showSendSellerMessageModal: Function;
    showWaitingForApprovalModal: Function;
};

const BidMessage = (props: Props) => {
    const { itemId, showSendSellerMessageModal, showWaitingForApprovalModal } = props;

    const { catalogId, lotNumber = '', sellerId = 0 } = useSelector((state) => getItem(state, itemId), isEqual);
    const seller = useSelector((state) => getSeller(state, sellerId), isEqual);
    const { open: isLotOpen } = useSelector((state) => getLiveItemStatus(state, itemId), isEqual);
    const { isCatalogOnly, saleStartTs } = useSelector((state) => getCatalog(state, catalogId), isEqual);
    const { title: catalogTitle } = useSelector((state) => getCatalog(state, catalogId), isEqual);
    const { bidderHasBid, bidderHasHighBid, isAvailable, isPassed, isReserveMet, isSold } = useSelector(
        (state) => getBiddingInfo(state, itemId),
        isEqual
    );
    const { approved, declined, limited, pending, registered } = useSelector(
        (state) => getCatalogRegistration(state, catalogId),
        isEqual
    );

    const registrationMessageOptions = { id: 'registration.issue', values: { catalogTitle } };
    const paymentMessageOptions = { id: 'registration.payment_issue', values: { lotNumber } };

    let bidMessage;
    let bidMessageIcon;
    let secondBidMessage;
    let thirdBidMessage;
    let specialMessage = null;
    let extraClassName = '';
    const isSaleToday = isToday(saleStartTs * 1000);

    if (isAvailable) {
        if (isCatalogOnly) {
            bidMessage = 'registration.preview_only';
            bidMessageIcon = <FontAwesomeIcon className={styles.icon} icon={faEye} />;
            specialMessage = <FormattedMessage id="registration.preview_only_description" />;
        } else if (declined) {
            bidMessage = 'registration.declined';
            specialMessage = (
                <DeclineMessage
                    seller={seller}
                    showSendSellerMessageModal={showSendSellerMessageModal(registrationMessageOptions)}
                />
            );
            extraClassName = styles.declined;
        } else if (pending || (!registered && bidderHasBid)) {
            // second condition if you place bid but didn't register for auction beforehand.  default catalogRegistration
            if (bidderHasBid && bidderHasHighBid && !isReserveMet) {
                bidMessage = 'registration.reserve_not_met';
                extraClassName = styles.closed;
                secondBidMessage = 'registration.reserve_raise_bid';
            } else if (bidderHasBid && !bidderHasHighBid && !isReserveMet) {
                bidMessage = 'registration.reserve_not_met';
                extraClassName = styles.closed;
                secondBidMessage = 'registration.reserve_raise_bid';
            } else if (bidderHasBid && bidderHasHighBid && isReserveMet) {
                if (approved) {
                    bidMessage = 'registration.leading_bid';
                    extraClassName = styles.leading;
                    secondBidMessage = 'registration.raise_bid';
                } else {
                    bidMessage = 'registration.leading_after_approval';
                    extraClassName = styles.pending;
                    specialMessage = (
                        <FormattedMessage
                            id="registration.pending_raise_bid"
                            values={{
                                link: (
                                    <Link onClick={showWaitingForApprovalModal}>
                                        <FormattedMessage id="registration.pending_raise_bid_tips" />
                                    </Link>
                                ),
                            }}
                        />
                    );
                }
            } else if (bidderHasBid && !bidderHasHighBid && isReserveMet) {
                bidMessage = 'registration.outbid';
                extraClassName = styles.outbid;
                secondBidMessage = 'registration.raise_bid';
            }
        } else if (approved || limited) {
            if (bidderHasBid && bidderHasHighBid && isReserveMet) {
                bidMessage = 'registration.leading_bid';
                extraClassName = styles.leading;
                secondBidMessage = 'registration.raise_bid';
            } else if (bidderHasBid && !bidderHasHighBid && isReserveMet) {
                bidMessage = 'registration.outbid';
                extraClassName = styles.outbid;
                secondBidMessage = 'registration.raise_bid';
            } else if (bidderHasBid && bidderHasHighBid && !isReserveMet) {
                bidMessage = 'registration.reserve_not_met';
                extraClassName = styles.closed;
                secondBidMessage = 'registration.falls_below';
                thirdBidMessage = 'registration.reserve_raise_bid';
            } else if (bidderHasBid && !bidderHasHighBid && !isReserveMet) {
                bidMessage = 'registration.reserve_not_met';
                secondBidMessage = 'registration.falls_below';
                thirdBidMessage = 'registration.reserve_raise_bid';
            }
        }
    } else {
        if (isCatalogOnly && isSaleToday) {
            bidMessage = 'registration.closing_today';
            specialMessage = <FormattedMessage id="registration.preview_only_description_short" />;
        } else if (bidderHasBid && bidderHasHighBid && isSold) {
            bidMessage = 'registration.lot_won';
            extraClassName = styles.leading;
            specialMessage = (
                <FormattedMessage
                    id="registration.lot_won_copy"
                    values={{
                        houseName: <span className={styles['seller-name']}>{seller.name}</span>,
                        link: (
                            <Link onClick={showSendSellerMessageModal(paymentMessageOptions)}>
                                <FormattedMessage id="registration.contact_this_auctioneer" />
                            </Link>
                        ),
                    }}
                />
            );
        } else if (bidderHasBid && (!bidderHasHighBid || isPassed) && (isSold || isPassed)) {
            bidMessage = 'registration.lot_lost';
            extraClassName = styles.outbid;
            if (!isPassed) {
                specialMessage = (
                    <FormattedMessage
                        id="registration.lot_lost_copy"
                        values={{
                            link: (
                                <Link
                                    target="_blank"
                                    to="https://help.liveauctioneers.com/article/272-tips-for-winning"
                                >
                                    <FormattedMessage id="registration.tips_for_winning" />
                                </Link>
                            ),
                        }}
                    />
                );
            }
        } else {
            bidMessage = 'registration.lot_closed';
            extraClassName = styles.closed;
            secondBidMessage = 'registration.no_longer_bid';
        }
    }

    // TODO: there is logic to show a message for a declined user, but the wrapping clause will not show it.
    // Do we want to show the declined message?
    const shouldShow = (bidderHasBid && !isLotOpen) || isCatalogOnly;

    return shouldShow ? (
        <div className={styles.register}>
            <div className={styles['register-body']}>
                <div className={styles.bidMessageIconRow}>
                    {bidMessageIcon ? bidMessageIcon : null}
                    {bidMessage && (
                        <DefaultMessage
                            extraClassName={extraClassName}
                            messageId="registration.bid_status"
                            strongMessageId={bidMessage}
                        />
                    )}
                </div>
                <span>
                    {specialMessage}
                    {secondBidMessage && <FormattedMessage id={secondBidMessage} />}
                    {thirdBidMessage && (
                        <>
                            {' '}
                            <FormattedMessage id={thirdBidMessage} />
                        </>
                    )}
                </span>
            </div>
        </div>
    ) : null;
};

export default React.memo<Props>(BidMessage, isEqual);
